import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Compass, Users } from 'lucide-react'

import { Kolom } from '@/components/layout/kolom'
import { PostCard } from '@/components/post-card'
import { PostComposer } from '@/components/post-composer'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { useFeed, useMyGroups } from '@/features/sosial/api'

type Tab = 'jelajah' | 'mengikuti'

const kunciTab = 'skava.lini-masa.tab'

/**
 * Tab terakhir diingat per peramban. Hanya kenyamanan: bila penyimpanan
 * diblokir, lini masa tetap terbuka di Jelajah.
 */
function tabTersimpan(): Tab {
  try {
    return localStorage.getItem(kunciTab) === 'mengikuti' ? 'mengikuti' : 'jelajah'
  } catch {
    return 'jelajah'
  }
}

export function LiniMasaPage() {
  // Jelajah menjadi bawaan: akun baru belum mengikuti siapa pun, dan lini
  // masa pertama yang kosong membuat Skava terlihat sepi padahal tidak.
  const [tab, setTabState] = useState<Tab>(tabTersimpan)
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useFeed(tab)
  const { data: grupSaya } = useMyGroups()

  const setTab = (nilai: Tab) => {
    setTabState(nilai)
    try {
      localStorage.setItem(kunciTab, nilai)
    } catch {
      // Diabaikan; tab tetap berganti untuk kunjungan ini.
    }
  }

  const posts = data?.pages.flatMap((p) => p.posts) ?? []
  const grup = grupSaya?.groups ?? []

  return (
    <Kolom
      judul={
        <div role="tablist" aria-label="Pilih lini masa" className="flex items-center gap-6">
          <h1 className="sr-only">Beranda</h1>
          {(['jelajah', 'mengikuti'] as const).map((nilai) => (
            <button
              key={nilai}
              type="button"
              role="tab"
              aria-selected={tab === nilai}
              onClick={() => setTab(nilai)}
              className={tab === nilai ? 'text-ink' : 'text-faint transition hover:text-ink'}
            >
              {nilai === 'jelajah' ? 'Jelajah' : 'Mengikuti'}
            </button>
          ))}
        </div>
      }
    >
      <PostComposer placeholder="Apa yang baru?" className="-mx-4 px-4 pt-4 md:-mx-6 md:px-6" />

      {/* Grup yang sudah dimasuki, sebagai jalan pintas. Hanya muncul bila
          ada; deretan kosong berisi ajakan bergabung akan menjadi iklan
          yang dilihat setiap kali lini masa dibuka. */}
      {grup.length > 0 ? (
        <div className="thin-scroll -mx-4 flex gap-2 overflow-x-auto border-b border-line px-4 py-3 md:-mx-6 md:px-6">
          {grup.map((g) => (
            <Link
              key={g.id}
              to="/app/grup/$slug"
              params={{ slug: g.slug }}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-[10px] border border-line px-3 py-1.5 text-sm font-semibold text-ink transition hover:bg-sunken"
            >
              <Users className="size-3.5 text-subtle" />
              {g.name}
            </Link>
          ))}
        </div>
      ) : null}

      {isLoading ? (
        <div className="space-y-4 pt-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <EmptyState
          className="py-14"
          icon={<Compass className="size-6" />}
          title={tab === 'mengikuti' ? 'Belum ada kiriman dari yang Anda ikuti' : 'Belum ada kiriman'}
          description={
            tab === 'mengikuti'
              ? 'Buka profil seseorang lalu tekan Ikuti, atau bergabung ke grup — kiriman mereka akan muncul di sini.'
              : 'Jadilah yang pertama menulis. Ceritakan karya yang sedang Anda kerjakan.'
          }
          action={
            tab === 'mengikuti' ? (
              <Link to="/app/cari">
                <Button>Cari orang</Button>
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div>
          {posts.map((post) => (
            <PostCard key={post.id} post={post} />
          ))}

          {hasNextPage ? (
            <Button
              variant="outline"
              className="mt-5 w-full"
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
            >
              {isFetchingNextPage ? 'Memuat…' : 'Muat lebih banyak'}
            </Button>
          ) : null}
        </div>
      )}
    </Kolom>
  )
}
